<script setup>
import { computed, shallowRef, watch } from 'vue';
import moment from 'moment';
import { useStore } from 'vuex';
import PageTabs from '@/components/Utils/PageTabs.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useConfirm } from '@/composables/useConfirm';
import { useToast } from '@/composables/useToast';
import { titleCase } from '@/utils/format';
import { groupBySex, surnameFirst } from '@/utils/names';

// Attendance for one class and subject. "Take attendance" records a day
// (everyone starts as present; one click changes a student's status) and
// can still change a day for two days after; "Monthly record" shows every
// recorded day of a month and prints the SF2. Both list students as
// "Last, First, Middle", A–Z, under Male and Female.
const props = defineProps({
  // Row from Class records: roomId, subjectId, subject_title, grade_level, room_section.
  classInfo: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const store = useStore();
const toast = useToast();
const { confirm } = useConfirm();

// Codes stored in student_attendance.attendance.
const STATUSES = [
  { code: 1, label: 'Present', short: 'P', variant: 'green' },
  { code: 2, label: 'Late', short: 'L', variant: 'amber' },
  { code: 0, label: 'Absent', short: 'A', variant: 'red' },
  { code: 3, label: 'Excused', short: 'E', variant: 'blue' },
];
const statusOf = (code) => STATUSES.find((s) => s.code === Number(code));
// A recorded day can still be changed for this many days after it.
const EDIT_DAYS = 2;
const today = () => moment().format('YYYY-MM-DD');
const minDate = moment().subtract(6, 'months').format('YYYY-MM-DD');

const TABS = [
  { id: 'take', name: 'Take attendance' },
  { id: 'month', name: 'Monthly record' },
];
const tab = shallowRef('take');

const date = shallowRef(today());
const rows = shallowRef([]);
const recorded = shallowRef(false);
const loading = shallowRef(false);
const saving = shallowRef(false);
const search = shallowRef('');

const schoolYear = computed(() => store.getters.getFilterSelected);
const classLine = computed(() =>
  [titleCase(props.classInfo?.subject_title), props.classInfo?.grade_level, props.classInfo?.room_section]
    .filter(Boolean)
    .join(' · '),
);

const editableUntil = computed(() => moment(date.value).add(EDIT_DAYS, 'days'));
const locked = computed(() => recorded.value && moment().isAfter(editableUntil.value, 'day'));

// Loads the day: what was recorded, or the class list with everyone present.
async function loadDay() {
  if (!props.classInfo || !date.value) return;
  loading.value = true;
  try {
    const { roomId, subjectId } = props.classInfo;
    const saved = await apiRequest('get', `/rooms-section/getAllAttendanceByDate/${date.value}/${roomId}/${subjectId}`);
    if (Array.isArray(saved) && saved.length) {
      rows.value = saved.map((r) => ({ ...r, attendance: Number(r.attendance) }));
      recorded.value = true;
    } else {
      const list = await apiRequest(
        'get',
        `/rooms-section/getMyStudentAttendance/${store.state.user.id}/${schoolYear.value}/${roomId}`,
      );
      rows.value = (Array.isArray(list) ? list : []).map((r) => ({ ...r, attendance: 1 }));
      recorded.value = false;
    }
  } catch (e) {
    rows.value = [];
    toast.error(errorMessage(e, 'Could not load the attendance.'));
  } finally {
    loading.value = false;
  }
}

watch(open, (isOpen) => {
  if (!isOpen) return;
  tab.value = 'take';
  date.value = today();
  search.value = '';
  month.value = moment().startOf('month');
  loadDay();
});
watch(date, (value) => {
  if (open.value && value) loadDay();
});

const rowKey = (row) => row.studentID ?? row.studentId ?? row.id;
const nameOf = (row) => surnameFirst(row);
const visibleRows = computed(() => {
  const q = search.value.trim().toLowerCase();
  return q ? rows.value.filter((r) => nameOf(r).toLowerCase().includes(q)) : rows.value;
});
const visibleGroups = computed(() => groupBySex(visibleRows.value));
const counts = computed(() =>
  STATUSES.map((s) => ({ ...s, count: rows.value.filter((r) => r.attendance === s.code).length })),
);

function setStatus(row, code) {
  if (locked.value) return;
  rows.value = rows.value.map((r) => (r === row ? { ...r, attendance: code } : r));
}
function markAll(code) {
  if (locked.value) return;
  rows.value = rows.value.map((r) => ({ ...r, attendance: code }));
}

async function save() {
  if (!rows.value.length || locked.value) return;
  const day = moment(date.value).format('MMM D, YYYY');
  const ok = await confirm({
    title: recorded.value ? `Update attendance for ${day}?` : `Save attendance for ${day}?`,
    description: 'Parents of students marked absent or late are notified.',
    confirmText: recorded.value ? 'Update' : 'Save',
  });
  if (!ok) return;

  saving.value = true;
  try {
    const body = { data: JSON.stringify(rows.value), subjectID: props.classInfo.subjectId };
    if (recorded.value) {
      assertOk(await apiRequest('patch', `/rooms-section/updateAttendance/${date.value}`, body));
    } else {
      assertOk(await apiRequest('post', '/rooms-section/studentAttendance', { ...body, attendanceDate: date.value }));
    }
    toast.success('Attendance saved.');
    emit('saved');
    await loadDay();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the attendance.'));
  } finally {
    saving.value = false;
  }
}

// Monthly record ---------------------------------------------------------
const month = shallowRef(moment().startOf('month'));
const monthRows = shallowRef([]);
const monthLoading = shallowRef(false);
let monthLoaded = false;

async function loadMonthData() {
  monthLoading.value = true;
  try {
    const { roomId, subjectId } = props.classInfo;
    const data = await apiRequest(
      'get',
      `/rooms-section/getAllAttendanceWholeSemester/${roomId}/${subjectId}/${schoolYear.value}`,
    );
    monthRows.value = Array.isArray(data) ? data : [];
    monthLoaded = true;
  } catch (e) {
    toast.error(errorMessage(e, 'Could not load the monthly record.'));
  } finally {
    monthLoading.value = false;
  }
}
watch(tab, (value) => {
  if (value === 'month' && !monthLoaded) loadMonthData();
});
watch(open, (isOpen) => {
  if (isOpen) monthLoaded = false;
});

const monthDays = computed(() => {
  const keys = new Set();
  monthRows.value.forEach((r) => Object.keys(r).forEach((k) => k !== 'student_name' && keys.add(k)));
  return [...keys]
    .filter((k) => moment(k, 'YYYY-MM-DD', true).isSame(month.value, 'month'))
    .sort();
});
const monthLabel = computed(() => month.value.format('MMMM YYYY'));
const monthGroups = computed(() => groupBySex(monthRows.value));
const monthTotals = (row) =>
  STATUSES.map((s) => ({ ...s, count: monthDays.value.filter((d) => Number(row[d]) === s.code && row[d] != null).length }));

function shiftMonth(step) {
  month.value = month.value.clone().add(step, 'month');
}

// SF2 for the month on screen, or for dates the teacher picks (up to
// SF2_MAX_DAYS, the most that fits the printed form; the server checks too).
const SF2_MAX_DAYS = 62;
const sf2Menu = shallowRef(false);
const rangeFrom = shallowRef('');
const rangeTo = shallowRef('');
watch(sf2Menu, (isOpen) => {
  if (!isOpen) return;
  rangeFrom.value = month.value.clone().startOf('month').format('YYYY-MM-DD');
  rangeTo.value = month.value.clone().endOf('month').format('YYYY-MM-DD');
});
const rangeError = computed(() => {
  if (!rangeFrom.value || !rangeTo.value) return 'Choose a start and end date.';
  const days = moment(rangeTo.value).diff(moment(rangeFrom.value), 'days') + 1;
  if (days < 1) return 'The start date must be on or before the end date.';
  if (days > SF2_MAX_DAYS) return `Choose ${SF2_MAX_DAYS} days or fewer.`;
  return '';
});

// Always sends exact dates (the whole month on screen by default), so the
// year printed is the one the teacher is looking at.
function printSF2(range = null) {
  const { roomId, subjectId } = props.classInfo;
  const from = range?.from ?? month.value.clone().startOf('month').format('YYYY-MM-DD');
  const to = range?.to ?? month.value.clone().endOf('month').format('YYYY-MM-DD');
  window.open(
    `${process.env.VUE_APP_SERVER}/pdf-generator/getSchoolForm2/${schoolYear.value}/${roomId}/${subjectId}/${month.value.format('M')}/${store.state.user.id}?from=${from}&to=${to}`,
    '_blank',
  );
  sf2Menu.value = false;
}
function printRange() {
  if (rangeError.value) return;
  printSF2({ from: rangeFrom.value, to: rangeTo.value });
}
</script>

<template>
  <UiDialog v-model="open" title="Attendance" :description="classLine" size="xl" :persistent="false">
    <PageTabs v-model="tab" :items="TABS" class="att-tabs" />

    <!-- Take attendance -->
    <template v-if="tab === 'take'">
      <div class="att-toolbar">
        <!-- No floating label or extra icon: the browser's date input brings
             its own calendar button, and a label would sit it higher than
             the search box and button beside it. -->
        <v-text-field
          v-model="date"
          type="date"
          :min="minDate"
          :max="today()"
          hide-details
          aria-label="Attendance date"
          class="att-toolbar__date"
        />
        <v-text-field
          v-model="search"
          placeholder="Search students"
          prepend-inner-icon="mdi-magnify"
          clearable
          hide-details
          class="att-toolbar__search"
        />
        <v-btn variant="outlined" :disabled="locked || !rows.length" @click="markAll(1)">Mark all present</v-btn>
      </div>

      <div class="att-summary">
        <UiBadge v-for="s in counts" :key="s.code" :variant="s.variant">{{ s.label }} {{ s.count }}</UiBadge>
        <span v-if="recorded && !locked" class="att-summary__note">
          Recorded. You can change it until {{ editableUntil.format('MMM D') }}.
        </span>
        <span v-else-if="locked" class="att-summary__note att-summary__note--locked">
          <v-icon size="16">mdi-lock-outline</v-icon>
          Recorded and locked. Days can only be changed for {{ EDIT_DAYS }} days.
        </span>
        <span v-else-if="rows.length" class="att-summary__note">Not recorded yet. Everyone starts as present.</span>
      </div>

      <div class="att-list" :class="{ 'att-list--locked': locked }">
        <div v-if="loading" class="att-empty">Loading…</div>
        <div v-else-if="!visibleRows.length" class="att-empty">
          {{ rows.length ? 'No students match your search.' : 'No enrolled students in this class.' }}
        </div>
        <template v-for="group in visibleGroups" v-else :key="group.sex">
          <div class="att-group">{{ group.sex }} <span class="att-group__count">{{ group.rows.length }}</span></div>
          <div v-for="(row, i) in group.rows" :key="rowKey(row)" class="att-row">
            <span class="att-row__index">{{ i + 1 }}</span>
            <span class="att-row__name">{{ nameOf(row) }}</span>
            <div class="att-seg" role="radiogroup" :aria-label="`Attendance for ${nameOf(row)}`">
              <button
                v-for="s in STATUSES"
                :key="s.code"
                type="button"
                role="radio"
                class="att-seg__btn"
                :class="[`att-seg__btn--${s.variant}`, { 'att-seg__btn--on': row.attendance === s.code }]"
                :aria-checked="row.attendance === s.code"
                :disabled="locked"
                :title="s.label"
                @click="setStatus(row, s.code)"
              >
                {{ s.label }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- Monthly record -->
    <template v-else>
      <div class="att-toolbar">
        <div class="att-month">
          <v-btn icon="mdi-chevron-left" size="small" variant="text" aria-label="Previous month" @click="shiftMonth(-1)" />
          <span class="att-month__label">{{ monthLabel }}</span>
          <v-btn icon="mdi-chevron-right" size="small" variant="text" aria-label="Next month" @click="shiftMonth(1)" />
        </div>
        <div class="att-legend">
          <span v-for="s in STATUSES" :key="s.code" class="att-legend__item">
            <span class="att-cell" :class="`att-cell--${s.variant}`">{{ s.short }}</span>{{ s.label }}
          </span>
        </div>
        <v-menu v-model="sf2Menu" location="bottom end" offset="4" :close-on-content-click="false">
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="outlined"
              prepend-icon="mdi-printer-outline"
              append-icon="mdi-chevron-down"
              class="att-toolbar__end"
            >
              Print SF2
            </v-btn>
          </template>
          <div class="sf2-menu" role="menu" aria-label="Print SF2">
            <button type="button" role="menuitem" class="sf2-menu__item" @click="printSF2()">
              <v-icon size="16">mdi-calendar-month-outline</v-icon>
              Monthly — {{ monthLabel }}
            </button>
            <div class="sf2-menu__sep" />
            <form class="sf2-menu__range" @submit.prevent="printRange">
              <div class="sf2-menu__label">Custom dates</div>
              <div class="sf2-menu__dates">
                <v-text-field v-model="rangeFrom" type="date" label="From" density="compact" hide-details />
                <v-text-field v-model="rangeTo" type="date" label="To" :min="rangeFrom" density="compact" hide-details />
              </div>
              <div v-if="rangeError" class="sf2-menu__error">{{ rangeError }}</div>
              <v-btn type="submit" color="primary" size="small" block :disabled="!!rangeError">
                Print these dates
              </v-btn>
            </form>
          </div>
        </v-menu>
      </div>

      <div v-if="monthLoading" class="att-empty">Loading…</div>
      <div v-else-if="!monthDays.length" class="att-empty">No attendance recorded in {{ monthLabel }}.</div>
      <div v-else class="att-grid">
        <table>
          <thead>
            <tr>
              <th class="att-grid__name">Student</th>
              <th v-for="d in monthDays" :key="d" class="att-grid__day">
                <span>{{ moment(d).format('ddd') }}</span>{{ moment(d).format('D') }}
              </th>
              <th class="att-grid__total">Totals</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in monthGroups" :key="group.sex">
              <tr class="att-grid__group">
                <td class="att-grid__name">{{ group.sex }} <span class="att-group__count">{{ group.rows.length }}</span></td>
                <td :colspan="monthDays.length + 1" />
              </tr>
              <tr v-for="row in group.rows" :key="row.studentID">
                <td class="att-grid__name">{{ nameOf(row) }}</td>
                <td v-for="d in monthDays" :key="d" class="att-grid__day">
                  <span
                    v-if="row[d] != null && statusOf(row[d])"
                    class="att-cell"
                    :class="`att-cell--${statusOf(row[d]).variant}`"
                    :title="statusOf(row[d]).label"
                  >
                    {{ statusOf(row[d]).short }}
                  </span>
                </td>
                <td class="att-grid__total">
                  <template v-for="s in monthTotals(row)" :key="s.code">
                    <span v-if="s.count" class="att-total">{{ s.short }} {{ s.count }}</span>
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Close</v-btn>
      <v-btn
        v-if="tab === 'take'"
        color="primary"
        :loading="saving"
        :disabled="locked || loading || !rows.length || !date"
        @click="save"
      >
        {{ recorded ? 'Update attendance' : 'Save attendance' }}
      </v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.att-tabs {
  margin-bottom: 16px;
}
.att-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.att-toolbar__date {
  flex: 0 0 180px;
}
/* Native date picker icon in the theme's muted colour, and in dark mode */
.att-toolbar__date :deep(input::-webkit-calendar-picker-indicator) {
  opacity: 0.6;
  cursor: pointer;
}
html.dark .att-toolbar__date :deep(input::-webkit-calendar-picker-indicator) {
  filter: invert(1);
}
.att-toolbar__search {
  flex: 1 1 200px;
}
.att-toolbar__end {
  margin-left: auto;
}

/* Print SF2 menu: the month on screen, or a custom date range. */
.sf2-menu {
  width: 300px;
  padding: 4px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
}
.sf2-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}
.sf2-menu__item .v-icon {
  color: var(--muted-foreground);
}
.sf2-menu__item:hover,
.sf2-menu__item:focus-visible {
  background-color: var(--accent);
}
.sf2-menu__sep {
  height: 1px;
  margin: 4px -4px;
  background: var(--border);
}
.sf2-menu__range {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px 8px 8px;
}
.sf2-menu__label {
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.sf2-menu__dates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.sf2-menu__error {
  font-size: 12px;
  color: rgb(var(--v-theme-error));
}

.att-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}
.att-summary__note {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  font-size: 13px;
  color: var(--muted-foreground);
}
.att-summary__note--locked {
  color: var(--foreground);
}

.att-list {
  max-height: 52vh;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.att-row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
}
.att-row + .att-row {
  border-top: 1px solid var(--border);
}
/* Male / Female heading above each group of students */
.att-group {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 6px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--accent);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
.att-row + .att-group {
  border-top: 1px solid var(--border);
}
.att-group__count {
  margin-left: 4px;
  font-weight: 500;
}
.att-row__index {
  font-size: 12px;
  color: var(--muted-foreground);
  text-align: right;
}
.att-row__name {
  overflow: hidden;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.att-empty {
  padding: 32px 16px;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}

/* Segmented status picker: one click sets the status */
.att-seg {
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  border-radius: 8px;
  background: var(--accent);
}
.att-seg__btn {
  padding: 4px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--secondary-foreground);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.att-seg__btn:hover:not(:disabled) {
  color: var(--foreground);
}
.att-seg__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--ring);
}
.att-seg__btn:disabled {
  cursor: default;
}
.att-seg__btn--on {
  background: var(--card);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.att-seg__btn--on.att-seg__btn--green { color: #047857; }
.att-seg__btn--on.att-seg__btn--amber { color: #b45309; }
.att-seg__btn--on.att-seg__btn--red { color: #b91c1c; }
.att-seg__btn--on.att-seg__btn--blue { color: #1d4ed8; }
/* Same palette as UiBadge, including its dark-mode colours */
html.dark .att-seg__btn--on.att-seg__btn--green { color: #6ee7b7; }
html.dark .att-seg__btn--on.att-seg__btn--amber { color: #fcd34d; }
html.dark .att-seg__btn--on.att-seg__btn--red { color: #fca5a5; }
html.dark .att-seg__btn--on.att-seg__btn--blue { color: #93c5fd; }
.att-list--locked .att-seg__btn:not(.att-seg__btn--on) {
  opacity: 0.45;
}

/* Monthly record */
.att-month {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.att-month__label {
  min-width: 130px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}
.att-legend {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: var(--muted-foreground);
}
.att-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.att-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}
.att-cell--green { background: #ecfdf5; color: #047857; }
.att-cell--amber { background: #fffbeb; color: #b45309; }
.att-cell--red { background: #fef2f2; color: #b91c1c; }
.att-cell--blue { background: #eff6ff; color: #1d4ed8; }
html.dark .att-cell--green { background: rgba(16, 185, 129, 0.15); color: #6ee7b7; }
html.dark .att-cell--amber { background: rgba(245, 158, 11, 0.15); color: #fcd34d; }
html.dark .att-cell--red { background: rgba(239, 68, 68, 0.15); color: #fca5a5; }
html.dark .att-cell--blue { background: rgba(59, 130, 246, 0.15); color: #93c5fd; }

.att-grid {
  max-height: 56vh;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.att-grid table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.att-grid th,
.att-grid td {
  padding: 6px 8px;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  white-space: nowrap;
}
.att-grid thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 500;
  color: var(--muted-foreground);
}
.att-grid__name {
  position: sticky;
  left: 0;
  z-index: 2;
  min-width: 180px;
  text-align: left;
  box-shadow: 1px 0 0 var(--border);
}
.att-grid thead .att-grid__name {
  z-index: 3;
}
.att-grid__day {
  text-align: center;
}
.att-grid__day span {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
}
.att-grid__total {
  text-align: left;
}
.att-total {
  margin-right: 8px;
  font-size: 12px;
  color: var(--muted-foreground);
}
.att-grid__group td {
  background: var(--accent);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
.att-grid tbody tr:last-child td {
  border-bottom: 0;
}

@media (max-width: 600px) {
  .att-row {
    grid-template-columns: minmax(0, 1fr);
  }
  .att-row__index {
    display: none;
  }
  .att-summary__note {
    margin-left: 0;
  }
}
</style>
